import React, { useState, useEffect } from "react";
import {
  Table,
  Form,
  Modal,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Badge,
  Alert,
  Tooltip,
  OverlayTrigger,
  InputGroup,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";
import "bootstrap/dist/css/bootstrap.min.css";
import * as XLSX from "xlsx";
import "./ComplaintStyle.css";
import EditComplaintModel from "./Editcomplaint_model";

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statusCounts, setStatusCounts] = useState({
    Pending: 0,
    Resolved: 0,
    "In Progress": 0,
  });

  // Search/filters
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  // Scroll to top button
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New UX states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "CreatedAt",
    direction: "desc",
  });

  const token = localStorage.getItem("authToken");
  const API_URL = process.env.REACT_APP_API_URL;
  const Mobile_API_URL =
    process.env.REACT_APP_MOBILE_API_URL ||
    "https://selfcare.americanholdings.lk";

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    try {
      if (token) {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role ? decoded.role.toLowerCase() : null);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
    }
  }, [token]);

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catForm, setCatForm] = useState({
    id: null,
    MainCategory: "",
    SubCategory: "",
    logToAmsoft: false,
  });
  const [categoryMaster, setCategoryMaster] = useState([]); // from /api/complaints/categories
  const [subSuggestions, setSubSuggestions] = useState([]);

  const fetchCategoryMaster = async () => {
    if (categoryMaster.length) return;
    try {
      const res = await axios.get(`${API_URL}/api/complaints/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Expecting rows like { MainCategory: "..." }
      const mains = Array.from(
        new Set((res.data || []).map((r) => r.MCatDesc).filter(Boolean)),
      );
      setCategoryMaster(mains);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Load Failed",
        text: "Could not load categories.",
      });
    }
  };

  const openCategoryModal = async (c) => {
    await fetchCategoryMaster();

    // build sub suggestions from current data for selected main
    const subs = Array.from(
      new Set(
        complaints
          .filter((x) => x.MainCategory === c.MCatDesc)
          .map((x) => x.SubCategory)
          .filter(Boolean),
      ),
    );

    setSubSuggestions(subs);
    setCatForm({
      id: c.Id,
      MainCategory: c.MCatDesc || "",
      SubCategory: c.SubCategory || "",
    });
    setCatModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCatModalOpen(false);
    setCatForm({
      id: null,
      MainCategory: "",
      SubCategory: "",
      logToAmsoft: false,
    });
    setSubSuggestions([]);
  };

  // When main changes, refresh sub suggestions from existing complaints
  const handleMainChange = (val) => {
    setCatForm((f) => ({ ...f, MainCategory: val }));
    const subs = Array.from(
      new Set(
        complaints
          .filter((x) => x.MainCategory === val)
          .map((x) => x.SubCategory)
          .filter(Boolean),
      ),
    );
    setSubSuggestions(subs);
  };

  // AMSOFT logging function
  const logToAmsoft = async (complaintData) => {
    try {
      // Pre-check: AMSOFT insert 500s if the customer has no route plan or
      // no current address on file, so catch that here with a clear message.
      const { data: check } = await axios.get(
        `${API_URL}/api/complaints/amsoft-check`,
        {
          params: {
            customerCode: complaintData.Customer_code,
            branchCode: complaintData.Branch_code,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!check.canLog) {
        const missing = [];
        if (!check.hasRoutePlan) missing.push("route plan");
        if (!check.hasCurrentAddress) missing.push("current address");
        Swal.fire({
          icon: "warning",
          title: "Cannot Log to AMSOFT",
          text: `This customer has no ${missing.join(" and ")} on file. Add it first, then retry.`,
        });
        return;
      }

      const response = await axios.post(
        `${Mobile_API_URL}/api/RawDbComplaints/insert`,
        {
          customerCode: complaintData.Customer_code,
          branchCode: complaintData.Branch_code,
          Category: complaintData.MainCategory,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: "success",
          title: "AMSOFT Logged",
          text: "Complaint has been successfully logged to AMSOFT system.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("AMSOFT logging error:", error);
      Swal.fire({
        icon: "warning",
        title: "AMSOFT Logging Failed",
        text:
          error?.response?.data?.message ||
          "Failed to log complaint to AMSOFT system.",
        timer: 3000,
      });
    }
  };

  const submitCategoryUpdate = async () => {
    if (!catForm.id || !catForm.MainCategory.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Main Category is required.",
      });
    }

    try {
      const { data } = await axios.put(
        `${API_URL}/api/complaints/${catForm.id}/category`,
        {
          MainCategory: catForm.MainCategory.trim(),
          SubCategory: catForm.SubCategory?.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Optimistically update in-memory lists
      setComplaints((prev) =>
        prev.map((c) =>
          c.Id === catForm.id
            ? {
                ...c,
                MainCategory:
                  data?.newValues?.MainCategory ?? catForm.MainCategory,
                SubCategory:
                  data?.newValues?.SubCategory ?? catForm.SubCategory,
              }
            : c,
        ),
      );
      setFilteredRequests((prev) =>
        prev.map((c) =>
          c.Id === catForm.id
            ? {
                ...c,
                MainCategory:
                  data?.newValues?.MainCategory ?? catForm.MainCategory,
                SubCategory:
                  data?.newValues?.SubCategory ?? catForm.SubCategory,
              }
            : c,
        ),
      );

      Swal.fire({
        icon: "success",
        title: "Category Updated",
        timer: 1500,
        showConfirmButton: false,
      });

      // Log to AMSOFT if checkbox is checked
      if (catForm.logToAmsoft) {
        const complaintData = complaints.find((c) => c.Id === catForm.id);
        if (complaintData) {
          await logToAmsoft(complaintData);
        }
      }

      closeCategoryModal();
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (error?.response?.status === 403
          ? "You don't have permission (AllowCategoryUpdate required)."
          : "Update failed.");
      Swal.fire({ icon: "error", title: "Error", text: msg });
    }
  };
  // Enhanced delivery request parser
  const parseDeliveryRequest = (description) => {
    if (!description) return null;

    const fullBottlesMatch = description.match(/Full Bottles:\s*(\d+)/i);
    const emptyBottlesMatch = description.match(/Empty Bottles:\s*(\d+)/i);
    const additionalInfoMatch = description.match(
      /Additional Information:\s*(.+)/i,
    );

    if (fullBottlesMatch || emptyBottlesMatch) {
      return {
        fullBottles: fullBottlesMatch ? parseInt(fullBottlesMatch[1]) : 0,
        emptyBottles: emptyBottlesMatch ? parseInt(emptyBottlesMatch[1]) : 0,
        additionalInfo: additionalInfoMatch
          ? additionalInfoMatch[1].trim()
          : "",
      };
    }
    return null;
  };

  // Enhanced description renderer
  const renderDescription = (complaint) => {
    if (complaint.MainCategory === "Delivery Request") {
      const parsed = parseDeliveryRequest(complaint.Description);
      if (parsed) {
        return (
          <div
            className="delivery-request-details p-2 bg-light rounded border"
            style={{ minWidth: "250px" }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="fas fa-user-plus text-success"></i>
              <strong className="text-success small">DELIVERY REQUEST</strong>
            </div>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center p-2 bg-white rounded">
                <div
                  className="d-flex align-items-center"
                  style={{ minWidth: "70px" }}
                >
                  <div
                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{ width: "18px", height: "18px" }}
                  >
                    <i
                      className="fas fa-user text-white"
                      style={{ fontSize: "8px" }}
                    ></i>
                  </div>
                  <span className="small text-muted fw-semibold">Full:</span>
                </div>
                <span className="small fw-bold text-dark ms-2 flex-grow-1">
                  {parsed.fullBottles}
                </span>
              </div>
              <div className="d-flex align-items-center p-2 bg-white rounded">
                <div
                  className="d-flex align-items-center"
                  style={{ minWidth: "70px" }}
                >
                  <div
                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{ width: "18px", height: "18px" }}
                  >
                    <i
                      className="fas fa-user text-white"
                      style={{ fontSize: "8px" }}
                    ></i>
                  </div>
                  <span className="small text-muted fw-semibold">Empty:</span>
                </div>
                <span className="small fw-bold text-dark ms-2 flex-grow-1">
                  {parsed.emptyBottles}
                </span>
              </div>
            </div>
            {parsed.additionalInfo && (
              <div className="small text-muted">
                <strong>Note:</strong> {parsed.additionalInfo}
              </div>
            )}
          </div>
        );
      }
    }
    return (
      <div className="description-text" style={{ maxWidth: "200px" }}>
        {complaint.Description || "N/A"}
      </div>
    );
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "In Progress":
        return "info";
      case "Resolved":
        return "success";
      default:
        return "secondary";
    }
  };

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Extracts date directly from the DB string to avoid timezone shifting
  const formatDbDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const datePart = String(dateStr).slice(0, 10); // "YYYY-MM-DD"
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  };

  // Fetch complaints only when both dates are set
  const fetchComplaints = async (start, end) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/complaints`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: start, endDate: end },
      });
      const data = response.data || [];
      const sorted = data.sort(
        (a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt),
      );
      setComplaints(sorted);
      setFilteredRequests(sorted);
      updateStatusCounts(sorted);
      setCurrentPage(1); // Reset to first page

      // Set unique main categories
      const mainCats = Array.from(
        new Set(sorted.map((c) => c.MainCategory).filter(Boolean)),
      );
      setMainCategories(mainCats);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load Complaints data.",
      });
    }
    setLoading(false);
  };

  // Watch date fields, fetch only when both are set
  useEffect(() => {
    if (startDate && endDate) {
      fetchComplaints(startDate, endDate);
    } else {
      // Clear lists when dates are changed/cleared
      setComplaints([]);
      setFilteredRequests([]);
      setMainCategories([]);
      setSubCategories([]);
      setSelectedMainCategory("");
      setSelectedSubCategory("");
      setSearchTerm("");
      setStatusCounts({ Pending: 0, Resolved: 0, "In Progress": 0 });
    }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  // Subcategory options update
  useEffect(() => {
    if (selectedMainCategory) {
      const filteredSubs = Array.from(
        new Set(
          complaints
            .filter((c) => c.MainCategory === selectedMainCategory)
            .map((c) => c.SubCategory)
            .filter(Boolean),
        ),
      );
      setSubCategories(filteredSubs);
    } else {
      setSubCategories([]);
    }
    setSelectedSubCategory("");
    // eslint-disable-next-line
  }, [selectedMainCategory, complaints]);

  // Status counts
  const updateStatusCounts = (complaintsData) => {
    const counts = { Pending: 0, Resolved: 0, "In Progress": 0 };
    complaintsData.forEach((complaint) => {
      if (counts[complaint.Status] !== undefined) counts[complaint.Status] += 1;
    });
    setStatusCounts(counts);
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredRequests].sort((a, b) => {
      if (direction === "asc") {
        return a[key] > b[key] ? 1 : -1;
      }
      return a[key] < b[key] ? 1 : -1;
    });
    setFilteredRequests(sorted);
  };

  // All filters logic
  const applyAllFilters = (
    data,
    { searchTerm, selectedMainCategory, selectedSubCategory },
  ) => {
    let filtered = [...data];
    // Search
    if (searchTerm) {
      const val = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.MainCategory || "").toLowerCase().includes(val) ||
          (c.SubCategory || "").toLowerCase().includes(val) ||
          (c.Description || "").toLowerCase().includes(val) ||
          (c.Customer_code || "").toLowerCase().includes(val) ||
          String(c.Cust_ID || "").includes(val),
      );
    }
    // Main Category
    if (selectedMainCategory) {
      filtered = filtered.filter(
        (c) => c.MainCategory === selectedMainCategory,
      );
    }
    // Sub Category
    if (selectedSubCategory) {
      filtered = filtered.filter((c) => c.SubCategory === selectedSubCategory);
    }
    return filtered;
  };

  // Handle search bar
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtered = applyAllFilters(complaints, {
      searchTerm: value,
      selectedMainCategory,
      selectedSubCategory,
    });
    setFilteredRequests(filtered);
    setCurrentPage(1); // Reset to first page
  };

  // Handle filters
  const handleMainCategoryChange = (e) => {
    const val = e.target.value;
    setSelectedMainCategory(val);
    const filtered = applyAllFilters(complaints, {
      searchTerm,
      selectedMainCategory: val,
      selectedSubCategory: "",
    });
    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleSubCategoryChange = (e) => {
    const val = e.target.value;
    setSelectedSubCategory(val);
    const filtered = applyAllFilters(complaints, {
      searchTerm,
      selectedMainCategory,
      selectedSubCategory: val,
    });
    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  // Reset filters (keeps date range)
  const handleResetFilters = () => {
    setSelectedMainCategory("");
    setSelectedSubCategory("");
    setSearchTerm("");
    setFilteredRequests(complaints);
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredRequests.slice(startIndex, endIndex);

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredRequests.map((complaint) => ({
      ID: complaint.Id,
      "Customer Code": complaint.Customer_code,
      "Branch Code": complaint.Branch_code,
      "Route Code": complaint.RouteCode,
      "Office Location": complaint.OfficeLocation,
      "Logged Date": formatDbDate(complaint.CreatedAt),
      "Main Category": complaint.MainCategory,
      "Sub Category": complaint.SubCategory,
      Description: complaint.Description,
      Status: complaint.Status,
      "Resolved At": formatDbDate(complaint.ResolvedAt),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Complaints");
    XLSX.writeFile(wb, "Complaints.xlsx");
  };

  // Notification (when resolving)
  const sendNotification = async (complaintId, customerId, customMessage) => {
    try {
      const url = `${Mobile_API_URL}/api/FirebasePushNotifications/resolveComplaintAndNotify?complaintId=${complaintId}&customerId=${customerId}&customMessage=${encodeURIComponent(
        customMessage,
      )}`;
      await axios.post(url, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Notification Sent",
        text: "Customer has been notified successfully.",
      });
    } catch (error) {
      if (error.response) {
        Swal.fire({
          icon: "error",
          title: "Notification Failed",
          text:
            error.response.data?.message ||
            `Server Error: ${error.response.status}`,
        });
      } else if (error.request) {
        Swal.fire({
          icon: "error",
          title: "Network Error",
          text: "No response from the server. Please check your internet connection.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Unexpected Error",
          text: error.message || "An unexpected error occurred.",
        });
      }
    }
  };

  // Status change with instant UI update
  const handleStatusChange = (complaintId, newStatus, customerId) => {
    if (newStatus === "Resolved") {
      setSelectedComplaint({ complaintId, customerId });
      setShowModal(true);
    } else {
      updateStatusAndFiltered(complaintId, newStatus);
    }
  };

  // Update status in main and filtered lists
  const updateStatusAndFiltered = async (complaintId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/complaints/status/${complaintId}`,
        { Status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setComplaints((prevComplaints) => {
        const updated = prevComplaints.map((complaint) =>
          complaint.Id === complaintId
            ? { ...complaint, Status: newStatus }
            : complaint,
        );
        updateStatusCounts(updated);
        setFilteredRequests((prevFiltered) =>
          prevFiltered.map((complaint) =>
            complaint.Id === complaintId
              ? { ...complaint, Status: newStatus }
              : complaint,
          ),
        );
        return updated;
      });
      Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: `Complaint status has been updated to ${newStatus}`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update complaint status.",
      });
    }
  };

  // Modal submit handler
  const handleSubmitResolved = async () => {
    if (customMessage.trim()) {
      setIsSubmitting(true);
      try {
        const { complaintId, customerId } = selectedComplaint;
        await sendNotification(complaintId, customerId, customMessage);
        await updateStatusAndFiltered(complaintId, "Resolved");
        setShowModal(false);
        setCustomMessage("");
        setSelectedComplaint(null);
      } catch (error) {
        console.error("Submission failed:", error);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "An error occurred while submitting the message.",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "Custom Message Required",
        text: "Please enter a custom message before proceeding.",
      });
    }
  };

  // Status filter card click
  const handleStatusFilter = (status) => {
    let filtered = complaints.filter(
      (complaint) => complaint.Status === status,
    );
    filtered = applyAllFilters(filtered, {
      searchTerm,
      selectedMainCategory,
      selectedSubCategory,
    });
    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleSetOneMonthRange = () => {
    const today = new Date();
    const end = formatDateForInput(today);

    const pastMonth = new Date();
    pastMonth.setMonth(pastMonth.getMonth() - 1);
    const start = formatDateForInput(pastMonth);

    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="page">
      {/* Header with improved styling */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">🎧 Customer Assistance Dashboard</h1>
          <p className="text-muted mb-0">
            Manage and track customer complaints efficiently
          </p>
        </div>
        {complaints.length > 0 && (
          <Badge bg="info" className="fs-6">
            Total: {complaints.length} | Showing: {filteredRequests.length}
          </Badge>
        )}
      </div>

      {/* Enhanced Date Selection */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Card.Title className="h6 mb-3">📅 Date Range Selection</Card.Title>
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold">Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="sm"
                max={endDate || ""}
                className="border-2"
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold">End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                min={startDate || ""}
                onChange={(e) => setEndDate(e.target.value)}
                size="sm"
                className="border-2"
              />
            </Col>
            <Col xs={12} md={6}>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    setStartDate(formatDateForInput(today));
                    setEndDate(formatDateForInput(tomorrow));
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const lastWeek = new Date(
                      today.getTime() - 7 * 24 * 60 * 60 * 1000,
                    );
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    setStartDate(formatDateForInput(lastWeek));
                    setEndDate(formatDateForInput(tomorrow));
                  }}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleSetOneMonthRange}
                >
                  Last Month
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      1,
                    );
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    setStartDate(formatDateForInput(firstDay));
                    setEndDate(formatDateForInput(tomorrow));
                  }}
                >
                  This Month
                </Button>
              </div>
            </Col>
            {loading && (
              <Col xs={12}>
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading data for {startDate} to {endDate}...
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Show rest of content only when data is loaded */}
      {complaints.length > 0 && (
        <>
          {/* Enhanced Status Cards */}
          <Row className="mb-4 g-3">
            {[
              { status: "Pending", icon: "⏳", color: "warning" },
              { status: "In Progress", icon: "🔄", color: "info" },
              { status: "Resolved", icon: "✅", color: "success" },
            ].map(({ status, icon, color }) => (
              <Col xs={6} md={4} key={status}>
                <Card
                  className={`border-${color} shadow-sm hover-card`}
                  style={{ cursor: "pointer", transition: "all 0.3s ease" }}
                  onClick={() => handleStatusFilter(status)}
                >
                  <Card.Body className="text-center">
                    <div className="fs-2 mb-2">{icon}</div>
                    <h6 className="card-title mb-1">{status}</h6>
                    <h4 className={`text-${color} mb-0`}>
                      {statusCounts[status] || 0}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Enhanced Search and Filters */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">🔍 Search & Filters</span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Always visible search */}
              <Row className="mb-3">
                <Col xs={12} md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by Category, Description, Customer Code, or ID..."
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setSearchTerm("");
                          setFilteredRequests(complaints);
                        }}
                      >
                        ✕
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                <Col xs={12} md={4} className="text-end">
                  <Button
                    variant="success"
                    onClick={exportToExcel}
                    className="d-flex align-items-center gap-2 ms-auto"
                  >
                    <i className="bi bi-download"></i>
                    Export to Excel
                  </Button>
                </Col>
              </Row>

              {/* Collapsible filters */}
              {showFilters && (
                <Row className="g-3">
                  <Col xs={12} md={4}>
                    <Form.Label className="fw-semibold">
                      Main Category
                    </Form.Label>
                    <Form.Select
                      value={selectedMainCategory}
                      onChange={handleMainCategoryChange}
                    >
                      <option value="">All Categories</option>
                      {mainCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Label className="fw-semibold">
                      Sub Category
                    </Form.Label>
                    <Form.Select
                      value={selectedSubCategory}
                      onChange={handleSubCategoryChange}
                      disabled={!selectedMainCategory}
                    >
                      <option value="">All Sub Categories</option>
                      {subCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={4} className="d-flex align-items-end">
                    <Button
                      variant="outline-danger"
                      onClick={handleResetFilters}
                      className="w-100"
                    >
                      Reset All Filters
                    </Button>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Results Summary */}
          {filteredRequests.length > 0 && (
            <Alert
              variant="info"
              className="d-flex justify-content-between align-items-center"
            >
              <span>
                📊 Showing{" "}
                <strong>
                  {startIndex + 1}-{Math.min(endIndex, filteredRequests.length)}
                </strong>{" "}
                of <strong>{filteredRequests.length}</strong> complaints
              </span>
              <Form.Select
                value={itemsPerPage}
                style={{ width: "auto" }}
                size="sm"
                disabled // Since itemsPerPage is const, but you can make it dynamic
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </Form.Select>
            </Alert>
          )}
        </>
      )}

      {/* Enhanced Table */}
      <div className="table-responsive">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">
              Loading your assistance requests...
            </p>
          </div>
        ) : complaints.length === 0 && startDate && endDate ? (
          <Alert variant="warning" className="text-center py-4">
            <h5>📭 No complaints found</h5>
            <p>
              No assistance requests were found for the selected date range.
            </p>
          </Alert>
        ) : complaints.length === 0 ? (
          <Alert variant="info" className="text-center py-4">
            <h5>🎯 Ready to get started</h5>
            <p>
              Please select a date range above to view customer assistance
              requests.
            </p>
          </Alert>
        ) : filteredRequests.length > 0 ? (
          <>
            <Table
              striped
              bordered
              hover
              responsive
              className="mb-0 shadow-sm"
              style={{ minWidth: "1200px" }}
            >
              <thead className="table-dark sticky-top">
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("Id")}
                  >
                    ID{" "}
                    {sortConfig.key === "Id" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th>Customer</th>
                  <th>Location</th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("CreatedAt")}
                  >
                    Date{" "}
                    {sortConfig.key === "CreatedAt" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Resolved</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((complaint) => (
                  <tr key={complaint.Id}>
                    <td>
                      <Badge bg="secondary">#{complaint.Id}</Badge>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold">
                          {complaint.Customer_code}
                        </div>
                        <small className="text-muted">
                          Branch: {complaint.Branch_code}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{complaint.OfficeLocation}</div>
                        <small className="text-muted">
                          Route: {complaint.RouteCode}
                        </small>
                      </div>
                    </td>
                    <td>{formatDbDate(complaint.CreatedAt)}</td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <div>
                          <Badge bg="primary" className="me-1">
                            {complaint.MainCategory}
                          </Badge>
                          {complaint.SubCategory && (
                            <Badge bg="secondary" className="small">
                              {complaint.SubCategory}
                            </Badge>
                          )}
                        </div>
                        {/* Show Edit Category for non-Delivery Request to all, for Delivery Request to admin only */}
                        {(complaint.MainCategory !== "Delivery Request" ||
                          userRole === "admin") && (
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => openCategoryModal(complaint)}
                          >
                            Edit Category
                          </Button>
                        )}
                      </div>
                    </td>

                    <td style={{ maxWidth: "250px" }}>
                      {renderDescription(complaint)}
                    </td>

                    <td>
                      <Form.Select
                        value={complaint.Status}
                        onChange={(e) =>
                          handleStatusChange(
                            complaint.Id,
                            e.target.value,
                            complaint.Cust_ID,
                          )
                        }
                        disabled={complaint.Status === "Resolved"}
                        className={`border-0 text-white fw-semibold bg-${getStatusColor(complaint.Status)}`}
                        size="sm"
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                      </Form.Select>
                    </td>
                    <td>
                      {complaint.ResolvedAt ? (
                        <span className="text-success">
                          ✅ {formatDbDate(complaint.ResolvedAt)}
                        </span>
                      ) : (
                        <span className="text-muted">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ← Previous
                </Button>

                <div className="d-flex gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                    ) {
                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum
                              ? "primary"
                              : "outline-primary"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          style={{ minWidth: "40px" }}
                        >
                          {pageNum}
                        </Button>
                      );
                    } else if (
                      pageNum === currentPage - 3 ||
                      pageNum === currentPage + 3
                    ) {
                      return <span key={pageNum}>...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next →
                </Button>
              </div>
            )}
          </>
        ) : (
          <Alert variant="warning" className="text-center py-4">
            <h5>🔍 No results found</h5>
            <p>
              No complaints match your current search criteria. Try adjusting
              your filters.
            </p>
            <Button variant="outline-primary" onClick={handleResetFilters}>
              Clear All Filters
            </Button>
          </Alert>
        )}
      </div>

      {/* Enhanced Scroll to Top Button */}
      {showScrollButton && (
        <OverlayTrigger
          placement="left"
          overlay={<Tooltip>Scroll to top</Tooltip>}
        >
          <Button
            className="position-fixed shadow-lg"
            style={{
              bottom: "2rem",
              right: "2rem",
              zIndex: 9999,
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #007bff, #0056b3)",
              border: "none",
              fontSize: "24px",
              transition: "all 0.3s ease",
            }}
            onClick={scrollToTop}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
            }}
          >
            ↑
          </Button>
        </OverlayTrigger>
      )}

      <EditComplaintModel
        show={catModalOpen}
        onHide={closeCategoryModal}
        catForm={catForm}
        setCatForm={setCatForm}
        categoryMaster={categoryMaster}
        subSuggestions={subSuggestions}
        handleMainChange={handleMainChange}
        submitCategoryUpdate={submitCategoryUpdate}
        complaintData={complaints.find((c) => c.Id === catForm.id)}
      />

      {/* Enhanced Modal for Custom Message */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-envelope me-2"></i>
            Send Resolution Notification
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            This message will be sent to the customer to notify them about the
            complaint resolution.
          </Alert>
          <Form.Group>
            <Form.Label className="fw-semibold">Custom Message *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Enter a personalized message for the customer..."
              className="border-2"
              style={{ resize: "none" }}
            />
            <Form.Text className="text-muted">
              Character count: {customMessage.length}/500
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setCustomMessage("");
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitResolved}
            disabled={isSubmitting || !customMessage.trim()}
            className="d-flex align-items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" />
                Sending...
              </>
            ) : (
              <>
                <i className="bi bi-send"></i>
                Send Notification
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Complaints;
